import { EndpointBuilder } from "@reduxjs/toolkit/query";
import { HTTP_METHODS } from "@src/constants";
import { RequestReplies } from "@src/types";
import {
  RoleRequest,
  RoleRequestOverview,
} from "@src/types/role_request_types";
import { message } from "antd";

import { baseApiSlice } from "./baseApiSlice";

export const projectRoleRequestsApi = baseApiSlice.injectEndpoints({
  endpoints: (builder: EndpointBuilder<any, any, any>) => ({
    /* GET endpoints */
    ...getEndpoints(builder),
    /* PUT endpoints */
    ...putEndpoints(builder),
    /* DELETE endpoints */
    ...deleteEndpoints(builder),
  }),
});

function getEndpoints(builder: EndpointBuilder<any, any, any>) {
  return {
    /**
     * Get role request by role request id
     */
    getRoleRequest: builder.query<RoleRequestOverview[], number>({
      query: () => `/v2/role-requests`,
      transformErrorResponse: async () => {
        message.error("Failed to get role request for team lead", 5);
      },
      providesTags: ["RoleRequest"],
    }),

    /**
     * Get role request by role request id
     */
    getRoleRequestById: builder.query<RoleRequest, number>({
      query: (roleRequestId) => `/v2/role-requests/${roleRequestId}`,
      transformErrorResponse: async () => {
        message.error("Failed to get role request by role request id", 5);
      },
      providesTags: ["RoleRequestById"],
    }),

    /**
     * Get assigned (proposed and confirmed) role requests by role id and/or employeeId
     */
    getAssignedProjectRoleRequestsOfStandardRole: builder.query<
      RequestReplies[],
      number
    >({
      query: (standardRoleId) =>
        `/project-role-requests/assigned-requests?roleId=${standardRoleId}`,
      transformErrorResponse: async () => {
        message.error(
          "Failed to get project role requests by standard role id",
          5
        );
      },
    }),

    /**
     * Get assigned (proposed and confirmed) role requests by role id and/or employeeId
     */
    getAssignedProjectRoleRequestsOfEmployee: builder.query<
      RequestReplies[],
      number
    >({
      query: (employeeId) =>
        `/project-role-requests/assigned-requests?employeeId=${employeeId}`,
      transformErrorResponse: async () => {
        message.error(
          "Failed to get project role requests by standard role id",
          5
        );
      },
    }),
  };
}

function putEndpoints(builder: EndpointBuilder<any, any, any>) {
  const baseMethod = HTTP_METHODS.PUT;

  return {
    /**
     * Accept role request by role request id
     */
    acceptRequestV2: builder.mutation<RoleRequest, number>({
      query: (requestId) => ({
        url: `/v2/role-requests/${requestId}/accept`,
        method: baseMethod,
      }),
      transformErrorResponse: async () => {
        message.error("Error accepting role request", 5);
      },
      invalidatesTags: ["RoleRequestById", "RoleRequests", "ResourcePlan"],
    }),
    /**
     * Decline role request by role request id
     */
    declineRequestV2: builder.mutation<RoleRequest, number>({
      query: (requestId) => ({
        url: `/v2/role-requests/${requestId}/decline`,
        method: baseMethod,
      }),
      transformErrorResponse: async () => {
        message.error("Error declining role request", 5);
      },
      invalidatesTags: ["RoleRequestById", "RoleRequests", "ResourcePlan"],
    }),
  };
}

function deleteEndpoints(builder: EndpointBuilder<any, any, any>) {
  const baseMethod = HTTP_METHODS.DELETE;

  return {
    /**
     * Delete role request by role request id
     */
    deleteRequestV2: builder.mutation<string, string>({
      query: (requestId) => ({
        url: `/v2/role-requests/${requestId}`,
        method: baseMethod,
      }),
      transformErrorResponse: async () => {
        message.error("Error deleting role request for project", 5);
      },
      invalidatesTags: ["TabNotifications", "ResourcePlan"],
    }),
  };
}

export const {
  /* GET endpoints */
  useGetRoleRequestByIdQuery,

  /* PUT endpoints */
  useAcceptRequestV2Mutation,
  useDeclineRequestV2Mutation,

  /* DELETE endpoints */
  useDeleteRequestV2Mutation,
} = projectRoleRequestsApi;
